import React, { useEffect, useMemo, useState } from "react";
import Step1Email from "./components/Step1Email";
import Step2Resume from "./components/Step2Resume";
import Step3PersonalDetails from "./components/Step3PersonalDetails";
import StepperTabs from "./components/StepperTabs";
import ThankYouCard from "./components/ThankYouCard";
import {
  BrowserRouter as Router,
  useNavigate,
  useLocation,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import InvalidUrl from "./components/InvalidUrl";

function FormRouterWrapper() {
  const navigate = useNavigate();
  const location = useLocation();

  const gaId = process.env.REACT_APP_GOOGLE_MEASUREMENT_ID;
  const gtmId = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID;
  const fbPixelId = process.env.REACT_APP_FB_PIXEL_ID;
  const GA_debug_mode = process.env.REACT_APP_GOOGLE_ANALYTICS_DEBUG_MODE;


  const [formData, setFormData] = useState({
    // Step 1
    email: "",

    // Step 2
    country: "Australia",
    industry: "Healthcare",
    qualification: "",
    resume_path: "",

    // Step 3
    title: "",
    gender: "",
    family_name: "",
    first_name: "",
    dob: "",
    address: "",
    state: "",
    suburb: "",
    postcode: "",
    home_number: "",
    mobile_number: "",
    transport: "",
    referral_source: "",
    office: "",
    expertise: "",
    experience: "",
    speciality: "",
    availability: "",
  });

  const isStep1Valid = () => formData.email.trim() !== "";

  const isStep2Valid = () =>
    formData.qualification.trim() !== "" &&
    formData.country.trim() !== "" &&
    formData.industry.trim() !== "";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // This will be called after successful submission
  const handleThankYou = () => {
    // Track final form submit event
    trackFormSubmission();

    navigate("/thank-you");
  };
  // Match current location to a step index for StepperTabs
  const pathToStep = {
    "/": 1,
    "/resume-upload": 2,
    "/personal-details": 3,
    "/thank-you": 4,
  };

  const validPaths = Object.keys(pathToStep);
  const step = pathToStep[location.pathname] || 1;

  const isAllowed = useMemo(() => {
    if (location.pathname === "/") return true;
    if (location.pathname === "/resume-upload") return isStep1Valid();
    if (
      location.pathname === "/personal-details" ||
      location.pathname === "/thank-you"
    ) {
      return isStep1Valid() && isStep2Valid();
    }
    return false;
  }, [location.pathname, formData]);

  useEffect(() => {
    // Inject GA
    if (gaId) {
      // Define gtag function before loading script
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      // Add GA script
      const gaScript = document.createElement("script");
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      gaScript.async = true;
      document.head.appendChild(gaScript);

      // Once script loaded, initialize GA config
      gaScript.onload = () => {
        window.gtag("js", new Date());
        window.gtag("config", gaId, {
          debug_mode: GA_debug_mode, // debug_mode=true to test in localhost, for production  debug_mode=false
        });
      };
    }

    // Inject Facebook Pixel
    if (fbPixelId) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );

      window.fbq("init", fbPixelId);
      window.fbq("track", "PageView");
    }
  }, [gaId, fbPixelId]);

  // Track step changes in GA and FB Pixel
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "step_change",
        step_number: step,
      });
    }

    if (window.fbq) {
      window.fbq("trackCustom", "StepChange", { step });
    }
  }, [step]);

  // Track form submission
  function trackFormSubmission() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "form_submitted",
        form_email: formData.email,
      });
    }

    if (window.fbq) {
      window.fbq("track", "Lead");
    }
  }

  useEffect(() => {
    if (gtmId) {
      // Avoid duplicate script injection
      if (!document.getElementById("gtm-script")) {
        const script = document.createElement("script");
        script.id = "gtm-script";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
        document.head.appendChild(script);
      }

      // Initialize dataLayer if not already
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "gtm.js",
        "gtm.start": new Date().getTime(),
      });

      // Add noscript fallback only once
      if (!document.getElementById("gtm-noscript")) {
        const noscript = document.createElement("noscript");
        noscript.id = "gtm-noscript";
        noscript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `;
        document.body.appendChild(noscript);
      }
    }
  }, [gtmId]);

  // Instant redirect before rendering
  if (!isAllowed) {
    if (location.pathname === "/resume-upload") {
      return <Navigate to="/" replace />;
    } else if (
      location.pathname === "/personal-details" ||
      location.pathname === "/thank-you"
    ) {
      return <Navigate to="/resume-upload" replace />;
    }
  }

  const navigateToStep = (stepIndex) => {
    const stepToPath = {
      1: "/",
      2: "/resume-upload",
      3: "/personal-details",
    };

    navigate(stepToPath[stepIndex]);
  };

  // Show "Invalid URL" if path is not recognized
  if (!validPaths.includes(location.pathname)) {
    return <InvalidUrl />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center">
      <div className="w-full max-w-3xl mx-auto px-4 py-8">
        {step === 4 ? (
          <ThankYouCard />
        ) : (
          <>
            <div className="bg-gradient-to-r from-blue-50 via-white to-blue-100 rounded-xl shadow p-4 mb-8">
              <StepperTabs step={step} setStep={navigateToStep} />
            </div>
            {step === 1 && (
              <Step1Email
                value={formData.email}
                onChange={(val) => handleChange("email", val)}
                onNext={() => navigate("/resume-upload")}
              />
            )}
            {step === 2 && (
              <Step2Resume
                formData={formData}
                setFormData={setFormData}
                onNext={() => navigate("/personal-details")}
                onBack={() => navigate("/")}
              />
            )}
            {step === 3 && (
              <Step3PersonalDetails
                formData={formData}
                setFormData={setFormData}
                onBack={() => navigate("/resume-upload")}
                onThankYou={handleThankYou}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        {/* Form routes handled inside FormRouterWrapper */}
        <Route path="/*" element={<FormRouterWrapper />} />
      </Routes>
    </Router>
  );
}
